import { Field } from "components";
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, FormControlLabel, Checkbox, Typography, IconButton } from "@mui/material";
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { RegisterFormValidation, IRegisterFormValidation } from "./RegisterForm.config";
import { USER_TYPE } from "types/User";
import { LoadingButton } from "components/Shared/LoadingButton/LoadingButton";
import { useRef, useState } from "react";

interface IRegisterFormProps {
  onCompletedForm: (values: IRegisterFormValidation & { type: USER_TYPE }) => void;
  isLoading: boolean;
}

export const RegisterForm = ({ onCompletedForm, isLoading }: IRegisterFormProps) => {
  const methods = useForm<IRegisterFormValidation>({
    resolver: zodResolver(RegisterFormValidation),
    mode: "onTouched",
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = methods;

  const onSubmitHandler: SubmitHandler<IRegisterFormValidation> = (values) => {
    onCompletedForm({ ...values, type: USER_TYPE.PERSON });
  };

  const [preview, setPreview] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState<number>(0); // <-- Add this

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log('file = ', file);

    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setPreview(base64String);
      setValue("photo", base64String);
    };
    reader.readAsDataURL(file);
  };

  return (
    <FormProvider {...methods}>
      <Box
        component='form'
        noValidate
        autoComplete='off'
        onSubmit={handleSubmit(onSubmitHandler)}
      >
        <Field.TextField
          name="email"
          label="Correo electrónico"
          type="email"
        />
        <Field.TextField
          name="name"
          label="Nombre"
        />
        <Field.TextField
          name="surname"
          label="Apellidos"
        />
        <Field.TextField
          name="phone"
          label="Teléfono"
        />
        <Field.TextField
          name="birthday"
          label="Fecha de nacimiento"
          type="date"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            sx: {
              // Make input text white
              color: "white",
              // Target the calendar icon in WebKit-based browsers (Chrome, Edge, Safari)
              '& input::-webkit-calendar-picker-indicator': {
                filter: 'invert(1)', // inverts black icon to white
              },
            },
          }}
        />
        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            width: "100%",
            height: 200,
            mb: 2,
            border: "2px dashed #ccc",
            borderRadius: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            backgroundColor: "#1e1e1e",
            color: "#fff",
            position: "relative",
            '&:hover': {
              borderColor: "#aaa",
            },
          }}
        >
          {preview ? (
            <>
              <Box
                component="img"
                src={preview}
                alt="Preview"
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  zIndex: 1,
                  boxShadow: '0 0 10px rgba(0,0,0,0.5)',
                }}
              />
              <IconButton
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: "white"
                }}
                onClick={(e) => {
                  e.stopPropagation(); // prevent triggering file input
                  setPreview(null);
                  setValue("photo", undefined); // clear form value
                  setInputKey(prev => prev + 1); // <-- Reset input
                }}>
                <DeleteIcon />
              </IconButton>
            </>
          ) : (
            <Typography variant="body1" sx={{ zIndex: 1 }}>
              Haz clic para subir una foto
            </Typography>
          )}

          <input
            key={inputKey} // <-- This forces React to remount the input element
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handlePhotoUpload}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              {...register("acceptTerms")}
              sx={{ color: "white", '&.Mui-checked': { color: "white" } }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "white" }}>
              Acepto los términos y condiciones
            </Typography>
          }
        />
        {errors.acceptTerms && (
          <Typography color="error" variant="caption">
            {errors.acceptTerms.message}
          </Typography>
        )}
        {errors.birthday && (
          <Typography color="error" variant="caption">
            {errors.birthday.message}
          </Typography>
        )}
        <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
          <LoadingButton isLoading={isLoading} label={"Regístrate"} type="submit" style={{ width: "50%", height: "50px" }} />
        </div>
      </Box>
    </FormProvider>
  );
};
